'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/transformers'
import * as DX from './exercises/deepen'

const ScalingExplorer = dynamic(() => import('../_components/widgets/TransformerWidgets').then(m => m.ScalingExplorer), { ssr: false })

const MODELS: [string, string, string, string][] = [
  ['Our tiny GPT', '60 thousand', '3 thousand characters', 'seconds, one laptop'],
  ['GPT-2 (2019)', '1.5 billion', '~10 billion tokens', 'days, dozens of chips'],
  ['GPT-3 (2020)', '175 billion', '~300 billion tokens', 'weeks, thousands of GPUs'],
  ['Frontier models (2025–26)', 'hundreds of billions to trillions', 'tens of trillions of tokens', 'months, tens of thousands of GPUs'],
]

export default function Scaling() {
  return (
    <Lesson>
      <Step>
        <H>From our toy to the frontier</H>
        <P>
          Architecturally, the model you just trained is a close cousin of the largest language models. What changes is scale —
          by staggering factors:
        </P>
        <div className="gd-card my-6 overflow-x-auto p-1">
          <table className="gd-table">
            <thead>
              <tr>
                <th>model</th>
                <th>parameters</th>
                <th>training data</th>
                <th>training</th>
              </tr>
            </thead>
            <tbody>
              {MODELS.map(r => (
                <tr key={r[0]}>
                  {r.map((c, i) => (
                    <td key={i} className={i === 0 ? 'font-semibold' : 'text-[14px]'}>
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Callout kind="note">Exact figures for the newest models are often not published; the last row gives the rough, publicly discussed order of magnitude.</Callout>
      </Step>

      <Step gate="q-power">
        <H>Scaling laws</H>
        <P>
          Around 2020, researchers found something remarkable. Plot the final loss against model size (or data, or compute) on{' '}
          <strong>log-log axes</strong>, and you get an almost perfectly straight line, across many orders of magnitude. That’s
          a <strong>power law</strong>:
        </P>
        <MB>{'L(N) \\approx a \\cdot N^{-k}'}</MB>
        <P>
          It means progress is <em>predictable</em>: train small models, fit the line, and forecast how good a model 100× bigger
          will be before spending the money. This predictability is a big part of why so much money flowed into training ever
          larger models.
        </P>
        <Exercise id="power-law" title="Fit a scaling law" {...EX.powerLaw} hints={[<>Take <C>Math.log</C> of both lists, fit a straight line, then <C>a = Math.exp(c)</C> and <C>k = −m</C>.</>]} />
        <Quiz
          id="q-power"
          question="With L ∝ N^(−0.14), how much does the loss drop when the model gets 10× bigger?"
          options={[
            { label: 'It’s multiplied by 10^(−0.14) ≈ 0.72 — about 28% lower', correct: true },
            { label: 'It drops by 10×', why: 'That would need k = 1. Real exponents are small, so gains are steady but expensive.' },
            { label: 'It doesn’t change', why: 'It changes, by a predictable factor each time size multiplies.' },
          ]}
        />
      </Step>

      <Step gate="q-chinchilla">
        <H>How to spend a compute budget</H>
        <P>
          Training cost (compute) is roughly <M>{'6 \\times N \\times D'}</M>: parameters times training tokens. With a fixed
          budget, you can build a big model and train it briefly, or a smaller one and train it longer. In 2022 the “Chinchilla”
          study fitted a loss formula over both, and found many large models had been <em>under-trained</em>: for the same compute,
          a smaller model on more data did better.
        </P>
        <MB>{'L(N, D) = E + \\frac{A}{N^{\\alpha}} + \\frac{B}{D^{\\beta}}'}</MB>
        <Figure n="9.10" caption="Fix a compute budget, then slide between ‘bigger model’ and ‘more data’. Using the published fit, the predicted loss has a clear sweet spot (green).">
          <ScalingExplorer />
        </Figure>
        <Quiz
          id="q-chinchilla"
          question={<>What does the constant <M>E</M> in the formula represent?</>}
          options={[
            { label: 'The loss floor: unpredictability left in text even with infinite model and data', correct: true },
            { label: 'The cost of training', why: 'Cost is compute. E is part of the loss.' },
            { label: 'The number of layers', why: 'Architecture details are folded into A, B, α and β.' },
          ]}
          explain="Language contains genuine uncertainty — even a perfect model can’t know exactly which word you’ll choose next. That irreducible part is E."
        />
        <Callout kind="why">
          Scale alone produced surprising new abilities: translation, arithmetic, coding, step-by-step reasoning — none of them
          explicitly trained.
        </Callout>
      </Step>

      <Step gate="chinchilla">
        <H>Size a model for your budget</H>
        <P>
          The Chinchilla result boils down to a rule of thumb: train on roughly <strong>20 tokens for every parameter</strong>.
          Combine that with the cost formula and you can size a model for any budget.
        </P>
        <Exercise id="chinchilla" title="optimal" {...DX.chinchilla} hints={[<><C>{'const N = Math.sqrt(C / 120)'}</C>, then <C>{'{ N, D: 20 * N }'}</C>.</>]} />
        <Callout kind="note">
          In practice many recent models are trained on far <em>more</em> than 20 tokens per parameter. A model that’s cheaper
          to run is worth some extra training cost when it will answer billions of questions. “Compute-optimal” only means
          optimal for the training budget.
        </Callout>
        <Callout kind="why">
          One project left in this module: training your own GPT, end to end. Then the final module: how raw next-token
          predictors are turned into helpful assistants, and what’s at the frontier now.
        </Callout>
      </Step>
    </Lesson>
  )
}
